export type WidgetPosition = "bottom-left" | "bottom-right" | "top-left" | "top-right"
export type WidgetTheme = "light" | "dark" | "auto"
export type WidgetSize = "compact" | "standard" | "large"
export type LauncherStyle = "solid" | "soft" | "gradient" | "outline"
export type WidgetMotion = "smooth" | "spring" | "slide" | "pop" | "fade" | "flip" | "zoom" | "snappy" | "none"
export type ChatbotStatus = "active" | "paused"
export type RuleType = "ALLOW" | "BLOCK"

export type SubmitStyle = "arrow" | "circle" | "square"
export type SubmitColorStyle = "neutral" | "primary"
export type InputShadow = "none" | "subtle" | "strong"
export type QuickActionIcon = "sparkles" | "bot" | "message" | "headphones" | "heart" | "star" | "zap" | "rocket" | "coffee" | "thumbsup" | "gem"
export interface QuickAction {
  id: string
  label: string
  icon: QuickActionIcon
  message: string
}

export interface ChatbotSettings {
  id: string
  publicKey: string
  name: string
  companyName: string
  assistantName: string
  chatTitle: string
  chatSubtitle: string
  chatSubtitleColor?: string
  chatSubtitleColorDark?: string
  welcomeMessage: string
  suggestedQuestions: string[]
  primaryColor: string
  primaryColorDark: string
  primaryGradientEnabled?: boolean
  primaryGradientStart?: string | null
  primaryGradientEnd?: string | null
  primaryGradientStartDark?: string | null
  primaryGradientEndDark?: string | null
  primaryGradientAngle?: number
  launcherColor: string
  launcherColorDark: string
  headerColor: string
  headerColorDark: string
  headerGradientEnabled?: boolean
  headerGradientStart?: string
  headerGradientEnd?: string
  headerGradientStartDark?: string
  headerGradientEndDark?: string
  headerGradientAngle?: number
  userMessageColor: string
  userMessageColorDark: string
  userMessageTextColor?: string
  userMessageTextColorDark?: string
  assistantMessageColor: string
  assistantMessageColorDark: string
  textColor: string
  textColorDark: string
  chatBackground: string
  chatBackgroundDark: string
  chatBackgroundGradientEnabled?: boolean
  chatBackgroundGradientStart?: string
  chatBackgroundGradientEnd?: string
  chatBackgroundGradientStartDark?: string
  chatBackgroundGradientEndDark?: string
  chatBackgroundGradientAngle?: number
  widgetTheme: WidgetTheme
  motionStyle?: WidgetMotion
  position: WidgetPosition
  avatar: string
  avatarUrl: string
  avatarRadius: number
  avatarColor?: string
  launcherIcon: string
  launcherStyle: LauncherStyle
  launcherGradientStart?: string
  launcherGradientEnd?: string
  launcherGradientAngle?: number
  launcherGradientStartDark?: string
  launcherGradientEndDark?: string
  launcherGlowEnabled?: boolean
  launcherGlowColor?: string
  launcherGlowColorDark?: string
  launcherSize: number
  launcherRadius: number
  widgetRadius: number
  widgetSize: WidgetSize
  windowWidth?: number
  windowHeight?: number
  showBranding: boolean
  showPoweredBy?: boolean
  messageFontSize?: number
  headerTitleFontWeight?: 400 | 500 | 600 | 700
  messageFontWeight?: 400 | 500 | 600 | 700
  submitStyle: SubmitStyle
  submitColorStyle: SubmitColorStyle
  inputRadius: number
  inputShadow: InputShadow
  showAttachIcon: boolean
  quickActions: QuickAction[]
  showPlusButton: boolean
  showMicIcon: boolean
  showToolsIcon: boolean
  placeholder: string
  calloutTitle: string
  calloutSubtitle: string
  calloutEnabled: boolean
  calloutDelaySeconds: number
  calloutColor: string
  calloutColorDark: string
  status: ChatbotStatus
  model: string
  systemPrompt: string
  repeatQuestionLimit: number
  repeatQuestionWindowMinutes: number
  createdAt: string
  updatedAt: string
}

export type PublicWidgetSettings = Omit<ChatbotSettings, "model" | "systemPrompt" | "companyName" | "repeatQuestionLimit" | "repeatQuestionWindowMinutes">

export interface SearchRule {
  id: string
  chatbotId: string
  domainId: string
  type: RuleType
  pattern: string
}

export interface DiscoveryProbe {
  url: string
  status: number | null
  ok: boolean
  reason?: string
}

export interface DiscoveryResult {
  pages: DiscoveredPage[]
  sitemapFound: boolean
  robotsTxt: DiscoveryProbe | null
  sitemaps: DiscoveryProbe[]
  manualUrlsFound: number
  warnings: string[]
}

export interface SearchDomain {
  id: string
  chatbotId: string
  origin: string
  hostname: string
  manualUrls: string[]
  createdAt: string
  rules: SearchRule[]
  pages: DiscoveredPage[]
  files: KnowledgeFile[]
  /** Buscas recentes escopadas neste domínio (limite do backend), não fatia das globais. */
  textSearches: TextSearch[]
}

export interface DiscoveredPage {
  id: string
  domainId: string
  url: string
  path: string
  title: string
  selected: boolean
  lastSeenAt: string
  cachedContent?: string | null
  cachedAt?: string | null
  fetchError?: string | null
}

export interface KnowledgeFile {
  visibility: "internal" | "public"
  id: string
  chatbotId?: string
  domainId: string | null
  originalName: string
  extension: string
  mimeType: string
  size: number
  extractedTextLength: number
  createdAt: string
  updatedAt: string
}

export interface TextSearch {
  id: string
  chatbotId: string
  domainId: string | null
  query: string
  resultCount: number
  createdAt: string
}

export type AiModelId = "gpt-6-luna" | "gpt-5-mini" | "gpt-4.1"

export interface AiModelOption {
  id: AiModelId
  name: string
  description: string
  context: string
  usage: string
}

export interface AiConfig {
  model: string
  systemPrompt: string
  models: AiModelOption[]
}

export interface InstallDomain {
  id: string
  hostname: string
  status: "active" | "paused"
}

/** Seções do painel que o dono pode liberar para um convidado. Espelha CHATBOT_SECTIONS no servidor. */
export const CHATBOT_SECTIONS = ["general", "appearance", "ai", "content", "analytics", "installation", "integrations", "users"] as const
export type ChatbotSection = (typeof CHATBOT_SECTIONS)[number]

export function isChatbotSection(value: unknown): value is ChatbotSection {
  return typeof value === "string" && (CHATBOT_SECTIONS as readonly string[]).includes(value)
}

export interface ChatbotAccess {
  isOwner: boolean
  isSuperadmin: boolean
  /** Seções liberadas para quem está vendo; dono e superadmin recebem todas. */
  sections: ChatbotSection[]
}

export interface ChatbotDetail extends ChatbotSettings {
  domains: SearchDomain[]
  files: KnowledgeFile[]
  installDomains: InstallDomain[]
  /** Janela global mais recente (compatibilidade); a contagem real por domínio está em `textSearchCounts`. */
  textSearches: TextSearch[]
  /** Contagem total de buscas por `domainId` (chave "global" para buscas sem domínio). */
  textSearchCounts: Record<string, number>
  aiModels?: AiModelOption[]
  /** Ausente em payload legado: nesse caso o painel trata como acesso total. */
  access?: ChatbotAccess
  stats: {
    conversations: number
    messages: number
    discoveredPages: number
    /** Contagem de domínios conectados; sobrevive ao corte de seções para o checklist da Instalação. */
    domains: number
  }
}

export interface SearchResult {
  title: string
  url: string
  path: string
  snippet?: string
  kind?: "page" | "file"
}

export interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  sources?: SearchResult[]
  authorType?: "genesys_agent" | "genesys_automation"
}

export interface AnalyticsSummary {
  range: { from: string; to: string }
  totals: {
    conversations: number
    conversationsWithMessages: number
    messages: number
    avgMessagesPerConversation: number
    balloonSessions: number
    conversionBalloonToConversation: number | null
    uniqueVisitors: number
    avgDurationMs: number | null
    closed: number
    abandoned: number
    errors: number
    promptTokens: number
    completionTokens: number
    totalTokens: number
    outboundClicks: number
    outboundSessions: number
    whatsappClicks: number
    whatsappSessions: number
    genesysHandoffs: number
  }
  perDay: Array<{ day: string; count: number; whatsappClicks?: number; genesysHandoffs?: number }>
  topLinks: Array<{ host: string; count: number }>
  topics: Array<{ term: string; count: number }>
  topicCoverage: {
    scope: "all_time"
    conversationsAllTime: number
    conversationsWithVisitorMessagesAllTime: number
    conversationsClassifiedAllTime: number
    visitorMessagesAnalyzedAllTime: number
    conversationsClassified: number
    visitorMessagesAnalyzed: number
    visitorMessagesSampled: number
    visitorMessageLimit: number | null
    visitorMessageBatchSize: number
    limited: false
  }
  prepared?: { balloonFunnel: boolean; topics: boolean }
}

export type CloudProvider = "google_drive" | "onedrive" | "dropbox"
export interface CloudProviderStatus { provider: CloudProvider; configured: boolean }
export interface CloudConnectionSummary { id: string; provider: CloudProvider; accountEmail: string | null }
export interface CloudContentSourceSummary {
  id: string
  provider: CloudProvider
  folderName: string
  visibility: "internal" | "public"
  lastSyncedAt: string | null
  nextSyncAt: string
  lastError: string | null
  createdAt: string
  fileCount: number
}
export interface CloudContentSourcesResponse {
  canManage: boolean
  providers: CloudProviderStatus[]
  connections: CloudConnectionSummary[]
  sources: CloudContentSourceSummary[]
}
export interface CloudRemoteFolder { id: string; name: string }

export interface ConversationListItem {
  id: string
  startedAt: string
  lastMessageAt: string
  messageCount: number
  status: "active" | "closed" | "abandoned" | "empty"
  lastMessage: { role: string; content: string; createdAt: string } | null
  sessionKey: string | null
}

export interface ConversationList {
  total: number
  page: number
  pageSize: number
  items: ConversationListItem[]
}

export interface ConversationDetail {
  id: string
  chatbotId: string
  visitorId: string
  pageUrl: string | null
  startedAt: string
  sessionKey: string
  messages: Array<{ id: string; role: string; content: string; createdAt: string }>
}
